import React, { useState, useEffect, useRef } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ScanLine, Upload, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';

const formatCurrencyInput = (value) => {
  let numbers = value.replace(/\D/g, '');
  if (!numbers) return '';
  let numValue = parseInt(numbers, 10) / 100;
  return numValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseCurrencyToNumber = (value) => {
  if (!value) return 0;
  let cleaned = String(value).replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const formatDateBR = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
};

const NovoBoleto = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [resultado, setResultado] = useState(null); // resposta de /upload-boleto
  const [salvo, setSalvo] = useState(null); // resposta de /confirmar-boleto
  const [confirming, setConfirming] = useState(false);
  const [categorias, setCategorias] = useState([]);

  const [formData, setFormData] = useState({
    descricao: '',
    categoria: '',
    fornecedor_nome: '',
    valor: '',
    data_vencimento: '',
    forma_pagamento: 'Boleto',
    observacoes: '',
  });

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const response = await axiosInstance.get(`/contas/categorias${company.id ? `?company_id=${company.id}` : ''}`);
      setCategorias((response.data.pagar || []).slice().sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })));
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  };

  const resetTudo = () => {
    setFile(null);
    setResultado(null);
    setSalvo(null);
    setFormData({
      descricao: '', categoria: '', fornecedor_nome: '', valor: '',
      data_vencimento: '', forma_pagamento: 'Boleto', observacoes: '',
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (selected) => {
    if (!selected) return;
    const nomeArquivo = selected.name.toLowerCase();
    const tiposAceitos = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!tiposAceitos.includes(selected.type) && !nomeArquivo.match(/\.(pdf|png|jpe?g)$/)) {
      toast.error('Envie um arquivo PDF ou imagem (PNG/JPG)');
      return;
    }
    setFile(selected);
  };

  const handleProcessar = async () => {
    if (!file || !company.id) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', company.id);
      const response = await axiosInstance.post('/contas/pagar/upload-boleto', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(response.data);
      if (!response.data.duplicado) {
        setFormData({
          descricao: response.data.fornecedor ? `Boleto - ${response.data.fornecedor}` : 'Boleto',
          categoria: '',
          fornecedor_nome: response.data.fornecedor || '',
          valor: formatCurrencyInput(String(Math.round(response.data.valor * 100))),
          data_vencimento: response.data.data_vencimento || '',
          forma_pagamento: 'Boleto',
          observacoes: '',
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao processar o boleto');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmar = async (e) => {
    e.preventDefault();
    if (!formData.categoria) {
      toast.error('Categoria é obrigatória.');
      return;
    }
    setConfirming(true);
    try {
      const response = await axiosInstance.post('/contas/pagar/confirmar-boleto', {
        company_id: company.id,
        descricao: formData.descricao,
        categoria: formData.categoria,
        valor: parseCurrencyToNumber(formData.valor),
        data_vencimento: formData.data_vencimento,
        linha_digitavel: resultado.linha_digitavel,
        fornecedor_nome: formData.fornecedor_nome || null,
        forma_pagamento: formData.forma_pagamento,
        observacoes: formData.observacoes || null,
        arquivo_base64: resultado.arquivo_base64,
        arquivo_nome: resultado.arquivo_nome,
      });
      setSalvo(response.data);
      toast.success('Conta a pagar criada com sucesso!');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao salvar a conta a pagar');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="novo-boleto" />

      <div className="flex-1 p-3 pt-14 md:p-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <SubscriptionCard user={user} />
            <div className="flex items-center gap-3 mt-4">
              <ScanLine className="text-cyan-400" size={28} />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Novo Boleto</h1>
                <p className="text-zinc-400 text-sm">Envie o boleto e a conta a pagar é criada automaticamente</p>
              </div>
            </div>
          </div>

          {/* Etapa 1: upload */}
          {!resultado && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6 space-y-4">
                <label
                  htmlFor="boleto-file-input"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-700 rounded-lg py-10 cursor-pointer hover:border-cyan-500 transition-colors"
                >
                  <Upload className="text-zinc-500" size={32} />
                  <span className="text-zinc-300 text-sm">
                    {file ? file.name : 'Clique para selecionar o boleto (PDF ou imagem)'}
                  </span>
                  <input
                    id="boleto-file-input"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                </label>

                <Button
                  onClick={handleProcessar}
                  disabled={!file || processing}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                >
                  {processing ? (
                    <><Loader2 className="mr-2 animate-spin" size={16} /> Lendo boleto...</>
                  ) : (
                    'Processar Boleto'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Etapa 2a: duplicado */}
          {resultado?.duplicado && (
            <Card className="bg-zinc-900 border-yellow-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-400">
                  <AlertCircle size={20} /> Conta a pagar já existe
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-zinc-300 text-sm">Este boleto já foi lançado anteriormente:</p>
                <div className="bg-zinc-800 rounded-lg p-4 space-y-1 text-sm">
                  <p><span className="text-zinc-400">Descrição:</span> {resultado.conta_existente.descricao}</p>
                  <p><span className="text-zinc-400">Valor:</span> R$ {Number(resultado.conta_existente.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <p><span className="text-zinc-400">Vencimento:</span> {formatDateBR(resultado.conta_existente.data_vencimento)}</p>
                  <p><span className="text-zinc-400">Status:</span> {resultado.conta_existente.status}</p>
                </div>
                <Button onClick={resetTudo} variant="outline" className="w-full border-zinc-700">
                  Enviar outro boleto
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Etapa 2b: confirmação */}
          {resultado && !resultado.duplicado && !salvo && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle>Confira os dados antes de salvar</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleConfirmar} className="space-y-4">
                  {resultado.codigo_barras_imagem_base64 && (
                    <div className="bg-white rounded-lg p-3 flex justify-center">
                      <img
                        src={`data:image/png;base64,${resultado.codigo_barras_imagem_base64}`}
                        alt="Código de barras do boleto"
                        className="max-w-full"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Descrição *</Label>
                    <Input
                      required
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div>
                    <Label>Fornecedor</Label>
                    <Input
                      value={formData.fornecedor_nome}
                      onChange={(e) => setFormData({ ...formData, fornecedor_nome: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valor *</Label>
                      <Input
                        required
                        type="text"
                        inputMode="numeric"
                        value={formData.valor}
                        onChange={(e) => setFormData({ ...formData, valor: formatCurrencyInput(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <Label>Vencimento *</Label>
                      <Input
                        required
                        type="date"
                        value={formData.data_vencimento}
                        onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Categoria *</Label>
                    <Select
                      required
                      value={formData.categoria}
                      onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.filter(cat => cat && cat.trim() !== '').map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Linha Digitável</Label>
                    <Input readOnly value={resultado.linha_digitavel} className="bg-zinc-800 border-zinc-700 text-zinc-400 text-xs" />
                  </div>

                  <div>
                    <Label>Observações</Label>
                    <Textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1 border-zinc-700" onClick={resetTudo}>
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={confirming} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                      {confirming ? <><Loader2 className="mr-2 animate-spin" size={16} /> Salvando...</> : 'Confirmar e Salvar'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Etapa 3: salvo */}
          {salvo && (
            <Card className="bg-zinc-900 border-green-700">
              <CardContent className="pt-6 text-center space-y-4">
                <CheckCircle className="mx-auto text-green-500" size={48} />
                <p className="text-lg font-semibold">Conta a pagar criada com sucesso!</p>
                <div className="flex gap-2">
                  <Button onClick={resetTudo} variant="outline" className="flex-1 border-zinc-700">
                    Enviar outro boleto
                  </Button>
                  <Button onClick={() => window.location.assign('/contas-pagar')} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                    Ver Contas a Pagar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default NovoBoleto;
